import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Loader2 } from "lucide-react";
import Button from "../../../../shared/ui/button/Button";
import { Tooltip } from "../../../../shared/ui/common/Tooltip";
import { useBillingPeriod, useSubscription } from "../../../subscription";
import { useAdoptTool, useRemoveTool } from "../../application/useMarketplace";
import {
    TherapyTool,
    accessEndsAt,
    isAnnualPrice,
    isPaidTool,
    toolPriceCents,
} from "../../domain/types";
import ConfirmDialog from "../../../../shared/ui/modal/ConfirmDialog";

/**
 * The server refusing a module because there is nothing to bill it to.
 *
 * 402 and nothing else: the account is fine, the request is fine, and what is
 * missing is a subscription to put the line on.
 */
const isPaymentRequired = (error: unknown): boolean =>
    (error as { response?: { status?: number } })?.response?.status === 402;
import { formatMoney, includedWithLabel } from "../price";
import { moduleName } from "../toolCopy";

interface AdoptButtonProps {
    tool: TherapyTool;
}

const AdoptButton: React.FC<AdoptButtonProps> = ({ tool }) => {
    const { t, i18n } = useTranslation();
    const { isPremium } = useSubscription();
    const adopt = useAdoptTool();
    const remove = useRemoveTool();
    // A priced module changes what the account pays, so both directions are
    // confirmed. A free one is a switch and needs no ceremony.
    const [confirming, setConfirming] = useState(false);
    const [dropping, setDropping] = useState(false);
    // Set when the server says this purchase has to go through a checkout.
    // Asked of it rather than guessed here: whether a line can be billed is a
    // fact about the subscription, and the catalogue does not carry one.
    const [checkoutRequired, setCheckoutRequired] = useState(false);

    const language = i18n.language || "es";
    const isPending = adopt.isPending || remove.isPending;
    const paid = isPaidTool(tool);
    // What this account would be charged, in the period it is billed in. A
    // module joins the subscription that is already running: a yearly plan is
    // charged the yearly price, prorated to what is left of the year. Saying
    // "5,00 € al mes" there would be a tenth of the truth.
    const period = useBillingPeriod();
    const yearly = isAnnualPrice(tool, period);
    // Before VAT, which Stripe adds at payment.
    const price = `${formatMoney(toolPriceCents(tool, period), language)} ${t(
        "subscription.billing.tax.suffix"
    )}`;
    // Dropped, and theirs until the period that paid for it runs out.
    const endsAt = accessEndsAt(tool);

    // Inside a centre there is nothing to press: the centre decides, and the
    // card already says what it decided.
    if (tool.managed_by_center) return null;

    // Nothing special is drawn for a tool that comes with the plan. It is
    // adopted like any other and switched off like any other — what used to
    // be here was a line of text where every other card has a button, which
    // both looked wrong and read as though there were nothing to be done.
    // The one difference is money, and that is decided below by `paid`.

    // Comes with another tool: adopting or removing it here would be refused,
    // so the card points at the tool that carries it instead.
    if (tool.included_with) {
        const label = includedWithLabel(t, tool.included_with);
        return (
            <Tooltip content={t("marketplace.includedWithTooltip")}>
                <span tabIndex={0} aria-label={label}>
                    <Button variant="outline" disabled className="min-h-[44px]">
                        {label}
                    </Button>
                </span>
            </Tooltip>
        );
    }

    // Switched off by the centre, whether or not they had adopted it before.
    if (tool.disabled_by_center) {
        return (
            <Tooltip content={t("marketplace.centerDisabledTooltip")}>
                <span tabIndex={0} aria-label={t("marketplace.centerDisabledTooltip")}>
                    <Button variant="outline" disabled className="min-h-[44px]">
                        {t("marketplace.adopt")}
                    </Button>
                </span>
            </Tooltip>
        );
    }

    // Dropped and still theirs. Nothing to remove twice: what is offered is
    // taking it back, because they are paying for it either way until then.
    if (tool.adopted && endsAt) {
        // The date is said beside "Adoptada" rather than under the button —
        // it is a fact about the state, not about the action, and stacked
        // under the button it pushed every card's action out of line.
        return (
            <Button
                variant="outline"
                disabled={isPending}
                loading={adopt.isPending}
                onClick={() => adopt.mutate(tool.id)}
                className="min-h-[44px]"
            >
                {t("marketplace.keep")}
            </Button>
        );
    }

    if (tool.adopted) {
        return (
            <>
                <Button
                    variant="outline"
                    disabled={isPending}
                    loading={remove.isPending}
                    onClick={() => (paid ? setDropping(true) : remove.mutate(tool.id))}
                    className="min-h-[44px]"
                >
                    {t("marketplace.remove")}
                </Button>

                {/* Said before it is pressed: they keep the module until the
                    renewal and nothing comes back. Finding that out after
                    pressing is how a support ticket starts. */}
                <ConfirmDialog
                    isOpen={dropping}
                    title={t("marketplace.dropConfirm.title", { tool: moduleName(t, tool) })}
                    message={t("marketplace.dropConfirm.message")}
                    confirmLabel={t("marketplace.dropConfirm.confirm")}
                    isBusy={remove.isPending}
                    onConfirm={() => {
                        remove.mutate(tool.id);
                        setDropping(false);
                    }}
                    onCancel={() => setDropping(false)}
                />
            </>
        );
    }

    // A module that is sold, and nothing here to bill it to. Adopting is not
    // something we can do for them and not something to refuse either: it is
    // the start of a purchase. They land on the subscription screen with this
    // module already ticked, free to add the other one, and buy the lot under
    // one checkout and one discount code.
    //
    // `checkoutRequired` covers the second half of this: an account holding
    // Plus without paying for it — by invitation — looks premium here and has
    // no subscription for the line to go on. The server answers 402 and says
    // so, rather than handing over a module nobody would ever be charged for.
    if ((!isPremium || checkoutRequired) && paid) {
        return (
            <Link to={`/subscription/billing?modules=${encodeURIComponent(tool.slug)}`}>
                <Button variant="primary" className="min-h-[44px]">
                    {t("marketplace.adopt")}
                </Button>
            </Link>
        );
    }

    // No plan yet, and this tool comes with one. Pressing Adopt is the start
    // of buying Plus, not a mistake to be refused: a disabled button under a
    // tooltip said "you cannot have this" where the honest answer is "this is
    // what Plus is for, here is where to get it".
    if (!isPremium) {
        return (
            <Tooltip content={t("marketplace.plusRequiredTooltip")}>
                <Link to="/subscription/billing">
                    <Button variant="primary" className="min-h-[44px]">
                        {t("marketplace.adopt")}
                    </Button>
                </Link>
            </Tooltip>
        );
    }

    return (
        <>
            {/* No price caption under the button: the chip at the top of the
                card already says what the module costs, and saying it twice
                pushed the button off the line every other card sits on. What
                adopting costs is still said before it happens — in the
                confirmation, which is the moment it matters. */}
            <Button
                variant="primary"
                disabled={isPending}
                startIcon={
                    adopt.isPending ? <Loader2 className="animate-spin" size={16} /> : undefined
                }
                onClick={() => (paid ? setConfirming(true) : adopt.mutate(tool.id))}
                className="min-h-[44px]"
            >
                {t("marketplace.adopt")}
            </Button>

            <ConfirmDialog
                isOpen={confirming}
                title={t("marketplace.paidConfirm.title", { tool: moduleName(t, tool) })}
                message={t(
                    yearly
                        ? "marketplace.paidConfirm.messageAnnual"
                        : "marketplace.paidConfirm.message",
                    { price }
                )}
                confirmLabel={t("marketplace.paidConfirm.confirm")}
                isBusy={adopt.isPending}
                onConfirm={() => {
                    adopt.mutate(tool.id, {
                        onError: (error: unknown) => {
                            if (isPaymentRequired(error)) setCheckoutRequired(true);
                        },
                    });
                    setConfirming(false);
                }}
                onCancel={() => setConfirming(false)}
            />
        </>
    );
};

export default AdoptButton;
